import { useEffect, useState } from "react";
import Layout, { PageHeader } from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useStore } from "../context/StoreContext";
import { toast } from "sonner";
import { SpeakerHigh, SpeakerSlash, Key, LockKey, IdentificationCard, ChartBar } from "@phosphor-icons/react";
import { InitialsBadge } from "../components/InitialsBadge";
import { setNotifySound } from "../components/NotificationBell";

const CARD = "bg-white border border-[#E5E7EB] rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.05)] p-5 sm:p-6";
const FIELD = "w-full h-10 px-3 rounded-xl border border-[#E5E7EB] bg-white text-[13.5px] focus:outline-none focus:border-[#1E5FEB] focus:ring-2 focus:ring-[#DBEAFE]";
const BTN = "h-10 px-5 rounded-xl bg-[#1E5FEB] hover:bg-[#1849C8] disabled:opacity-60 text-white text-[13px] font-bold transition-colors";

function Title({ icon: Icon, children, sub }) {
  return (
    <div className="mb-4">
      <div className="flex items-center gap-2 text-[15px] font-bold text-slate-900"><Icon size={17} weight="bold" className="text-[#1E5FEB]" />{children}</div>
      {sub && <div className="text-[12px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function Detail({ label, value, testId }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-[0.16em] font-bold text-slate-500 mb-1">{label}</div>
      <div className="text-[14px] font-medium text-slate-900 break-words" data-testid={testId}>{value || "—"}</div>
    </div>
  );
}

function SoundCard() {
  const [on, setOn] = useState(null);
  useEffect(() => { api.get("/users/me/prefs").then((r) => setOn(r.data.notify_sound)).catch(() => setOn(true)); }, []);
  const toggle = async () => {
    const next = !on;
    setOn(next);
    try { await api.put("/users/me/prefs", { notify_sound: next }); setNotifySound(next); toast.success(next ? "Sound on" : "Sound muted — pop-ups still show"); }
    catch (e) { setOn(!next); toast.error(formatApiError(e)); }
  };
  return (
    <div className={CARD} data-testid="profile-sound-card">
      <Title icon={on ? SpeakerHigh : SpeakerSlash} sub="Pop-ups for messages, tasks and mentions always show. This only controls the chime.">Notification sound</Title>
      <button onClick={toggle} disabled={on === null} data-testid="profile-sound-toggle" role="switch" aria-checked={!!on}
        className="flex items-center gap-3">
        <span className={`w-12 h-7 rounded-full p-1 transition-colors ${on ? "bg-[#1E5FEB]" : "bg-slate-300"}`}>
          <span className={`block w-5 h-5 rounded-full bg-white shadow transition-transform ${on ? "translate-x-5" : ""}`} />
        </span>
        <span className="text-[13.5px] font-semibold text-slate-800">{on ? "Chime on" : "Chime muted"}</span>
      </button>
    </div>
  );
}

function PasswordCard() {
  const [f, setF] = useState({ cur: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (f.next.length < 8) return toast.error("New password must be at least 8 characters");
    if (f.next !== f.confirm) return toast.error("New passwords don't match");
    setBusy(true);
    try { await api.post("/users/me/password", { current_password: f.cur, new_password: f.next }); toast.success("Password changed"); setF({ cur: "", next: "", confirm: "" }); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  return (
    <form onSubmit={submit} className={CARD} data-testid="profile-password-card">
      <Title icon={LockKey} sub="Use at least 8 characters.">Change my password</Title>
      <div className="grid sm:grid-cols-3 gap-3">
        <input type="password" placeholder="Current password" value={f.cur} onChange={set("cur")} className={FIELD} data-testid="profile-pwd-current" autoComplete="current-password" />
        <input type="password" placeholder="New password" value={f.next} onChange={set("next")} className={FIELD} data-testid="profile-pwd-new" autoComplete="new-password" />
        <input type="password" placeholder="Confirm new password" value={f.confirm} onChange={set("confirm")} className={FIELD} data-testid="profile-pwd-confirm" autoComplete="new-password" />
      </div>
      <button disabled={busy || !f.cur} className={`${BTN} mt-4`} data-testid="profile-pwd-save">{busy ? "Saving…" : "Change password"}</button>
    </form>
  );
}

function PinCard({ hasPin }) {
  const [f, setF] = useState({ cur: "", usePwd: !hasPin, next: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!/^\d{4}$/.test(f.next)) return toast.error("New PIN must be exactly 4 digits");
    if (f.next !== f.confirm) return toast.error("New PINs don't match");
    setBusy(true);
    try {
      await api.post("/users/me/change-staff-pin", { new_pin: f.next, ...(f.usePwd ? { current_password: f.cur } : { current_pin: f.cur }) });
      toast.success("Staff PIN changed"); setF({ cur: "", usePwd: false, next: "", confirm: "" });
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: k === "cur" && !p.usePwd ? e.target.value.replace(/\D/g, "").slice(0, 4) : k === "cur" ? e.target.value : e.target.value.replace(/\D/g, "").slice(0, 4) }));
  return (
    <form onSubmit={submit} className={CARD} data-testid="profile-pin-card">
      <Title icon={Key} sub="Your 4-digit PIN confirms actions like editing notes or deleting tickets.">Change my Staff PIN</Title>
      <div className="grid sm:grid-cols-3 gap-3">
        <input type="password" inputMode={f.usePwd ? "text" : "numeric"} placeholder={f.usePwd ? "Login password" : "Current PIN"} value={f.cur} onChange={set("cur")} className={FIELD} data-testid="profile-pin-current" />
        <input type="password" inputMode="numeric" placeholder="New 4-digit PIN" value={f.next} onChange={set("next")} className={FIELD} data-testid="profile-pin-new" />
        <input type="password" inputMode="numeric" placeholder="Confirm new PIN" value={f.confirm} onChange={set("confirm")} className={FIELD} data-testid="profile-pin-confirm" />
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-4">
        <button disabled={busy || !f.cur} className={BTN} data-testid="profile-pin-save">{busy ? "Saving…" : "Change PIN"}</button>
        {hasPin && (
          <button type="button" onClick={() => setF((p) => ({ ...p, usePwd: !p.usePwd, cur: "" }))} className="text-[12.5px] font-semibold text-[#1E5FEB] hover:underline" data-testid="profile-pin-use-password">
            {f.usePwd ? "Use my current PIN instead" : "Forgot PIN? Use my login password"}
          </button>
        )}
      </div>
    </form>
  );
}

function SummaryCard() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/users/me/summary").then((r) => setS(r.data)).catch(() => {}); }, []);
  const items = s ? [
    ["Tickets assigned (not picked up)", s.assigned_open, "summary-assigned-open"], ["Open tasks", s.tasks_open, "summary-tasks-open"],
    ["Tasks done this week", s.tasks_done_week, "summary-tasks-done"], ["Notes written this week", s.notes_week, "summary-notes-week"],
    ["Tickets booked this week", s.tickets_booked_week, "summary-booked-week"], ["Messages sent this week", s.messages_sent_week, "summary-messages-week"],
  ] : [];
  return (
    <div className={CARD} data-testid="profile-summary-card">
      <Title icon={ChartBar} sub="Last 7 days">My work summary</Title>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {!s && <div className="text-[13px] text-slate-400">Loading…</div>}
        {items.map(([l, v, id]) => (
          <div key={id} className="rounded-xl border border-[#F1F5F9] bg-[#FAFBFC] px-4 py-3">
            <div className="text-[22px] font-bold text-slate-900 tabular-nums" data-testid={id}>{v}</div>
            <div className="text-[11.5px] text-slate-500">{l}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function MyProfile() {
  const { user } = useAuth();
  const { stores } = useStore();
  const storeName = (id) => stores.find((x) => x.id === id)?.name;
  const extra = (user?.store_ids || []).filter((id) => id !== user?.store_id).map(storeName).filter(Boolean);
  return (
    <Layout>
      <PageHeader overline="My profile" title={user?.name || "My profile"} />
      <div className="px-4 sm:px-6 md:px-8 pb-10 pt-2 grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
        <div className={`${CARD} xl:col-span-2`} data-testid="profile-details-card">
          <div className="flex items-center gap-4 mb-5">
            <InitialsBadge name={user?.name} size={56} />
            <div>
              <div className="text-[18px] font-bold text-slate-900">{user?.name}</div>
              <div className="text-[12px] text-slate-500 flex items-center gap-1"><IdentificationCard size={13} weight="bold" />Details are managed by an admin in Settings</div>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Detail label="Email" value={user?.email} testId="profile-email" />
            <Detail label="Role" value={user?.role === "admin" ? "Admin" : "Staff"} testId="profile-role" />
            <Detail label="Home store" value={user?.store_id ? storeName(user.store_id) : user?.role === "admin" ? "All stores" : "—"} testId="profile-home-store" />
            <Detail label="Other stores" value={extra.join(", ") || "—"} testId="profile-other-stores" />
          </div>
        </div>
        <SummaryCard />
        <SoundCard />
        <PasswordCard />
        <PinCard hasPin={!!user?.has_staff_pin} />
      </div>
    </Layout>
  );
}
